"use client";
import React, { useEffect, useState } from "react";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Users,
  Calendar,
  DollarSign,
  BookOpen,
  FileText,
} from "lucide-react";
import { useSubscribedUsers } from "../context/SubscribedUserContext";

const cx = (...c) => c.filter(Boolean).join(" ");

const Pill = ({ children, tone = "gray", size = "md" }) => {
  const toneMap = {
    red: "bg-red-50 text-red-700 ring-red-100",
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    gray: "bg-gray-50 text-gray-700 ring-gray-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    violet: "bg-violet-50 text-violet-700 ring-violet-100",
    yellow: "bg-yellow-50 text-yellow-700 ring-yellow-100",
  };
  const sizeMap = {
    sm: "px-2.5 py-1 text-[11px]",
    md: "px-3 py-1.5 text-xs",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full font-extrabold ring-1 whitespace-nowrap",
        toneMap[tone] || toneMap.gray,
        sizeMap[size] || sizeMap.md
      )}
    >
      {children}
    </span>
  );
};

const SectionDivider = ({ label, right }) => (
  <div className="flex items-center gap-3 my-8">
    {label ? (
      <span className="inline-flex items-center rounded-full bg-white px-4 py-2 text-xs font-extrabold text-gray-800 ring-1 ring-gray-200 shadow-sm">
        {label}
      </span>
    ) : null}
    <div className="h-px flex-1 bg-gradient-to-r from-gray-200 via-gray-200 to-transparent" />
    {right ? <div className="flex items-center gap-2">{right}</div> : null}
  </div>
);

export default function SubscribedUsersPage() {
  const { users, loading, fetchUsers, updateUserStatus } = useSubscribedUsers();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  
  const ITEMS_PER_PAGE = 5;

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = users.filter((user) =>
    Object.values(user).some((val) =>
      String(val).toLowerCase().includes(search.toLowerCase())
    )
  );

  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const start = (page - 1) * ITEMS_PER_PAGE;
  const currentUsers = filteredUsers.slice(start, start + ITEMS_PER_PAGE);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-[#fff7f6] to-[#f9fafb]">
      <div className="p-6 sm:p-8">
        {/* HERO SECTION */}
        <div className="rounded-3xl bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden mb-8">
          <div className="relative p-7 sm:p-10">
            <div className="pointer-events-none absolute -top-28 -left-28 h-72 w-72 rounded-full bg-blue-200/25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-28 -right-28 h-72 w-72 rounded-full bg-green-200/25 blur-3xl" />

            <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div className="max-w-3xl">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tone="blue">
                    <Users className="h-4 w-4 mr-1" />
                    User Subscriptions
                  </Pill>
                  <Pill tone="gray">Admin Portal</Pill>
                  <Pill tone="green">
                    <Sparkles className="h-4 w-4 mr-1" />
                    Active Subscribers
                  </Pill>
                </div>

                <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900">
                  Subscribed Users
                </h1>
                <p className="mt-3 text-gray-600 text-sm sm:text-base">
                  Monitor and manage user subscriptions, track engagement, and analyze subscription patterns across your platform.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-gray-50 ring-1 ring-gray-200 px-4 py-3">
                  <div className="text-[11px] font-extrabold tracking-widest uppercase text-gray-600">Total</div>
                  <div className="text-sm font-semibold text-gray-900 mt-1">
                    {filteredUsers.length} subscribers
                  </div>
                </div>

                <div className="rounded-2xl bg-green-50 ring-1 ring-green-200 px-4 py-3">
                  <div className="text-[11px] font-extrabold tracking-widest uppercase text-green-600">Revenue</div>
                  <div className="text-sm font-semibold text-green-900 mt-1">
                    ₹{users.reduce((sum, user) => sum + (parseFloat(user.doc_price) || 0), 0).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <SectionDivider
          label="Browse Subscribed Users"
          right={
            <div className="flex items-center gap-2">
              {search && <Pill tone="amber" size="sm">Search: {search}</Pill>}
              <Pill tone="blue" size="sm">Page {page} of {totalPages}</Pill>
            </div>
          }
        />

        {/* SEARCH */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>
        </div>

        {/* TABLE */}
        <div className="rounded-3xl bg-white ring-1 ring-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    User Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Course
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Document
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Subscription Info
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent"></div>
                        <span className="text-sm text-gray-600">Loading subscribed users...</span>
                      </div>
                    </td>
                  </tr>
                )}

                {!loading && currentUsers.map((user, index) => (
                  <tr
                    key={user.id}
                    className={cx(
                      "hover:bg-gray-50 transition-colors",
                      index !== currentUsers.length - 1 ? "border-b border-gray-100" : ""
                    )}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-2xl bg-blue-100 flex items-center justify-center">
                          <Users className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-extrabold text-gray-900">{user.user_name}</div>
                          <div className="text-sm text-gray-600">Subscriber ID: {user.id}</div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-2xl bg-green-100 flex items-center justify-center">
                          <BookOpen className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{user.course_name}</div>
                          <Pill tone="green" size="sm">Course</Pill>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-violet-600" />
                          <span className="font-medium text-gray-900">{user.document_title}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-semibold text-green-700">₹{user.doc_price}</span>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-amber-600" />
                          <span className="text-sm text-gray-700">{user.subscribed_at}</span>
                        </div>
                        <Pill tone="blue" size="sm">Active Subscription</Pill>
                      </div>
                    </td>
                  </tr>
                ))}

                {!loading && currentUsers.length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                          <Users className="h-6 w-6 text-gray-400" />
                        </div>
                        <div>
                          <div className="font-extrabold text-gray-900">No subscribed users found</div>
                          <p className="mt-1 text-sm text-gray-600">
                            {search ? "Try adjusting your search terms." : "No users have subscribed yet."}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-3 mt-8">
            <button
              onClick={() => setPage(Math.max(page - 1, 1))}
              disabled={page === 1}
              className={cx(
                "p-3 rounded-2xl ring-1 ring-gray-200 transition-colors",
                page === 1
                  ? "opacity-50 cursor-not-allowed bg-gray-50"
                  : "bg-white hover:bg-gray-50"
              )}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={cx(
                    "px-4 py-2 rounded-2xl text-sm font-extrabold transition-colors",
                    pageNum === page
                      ? "bg-red-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50 ring-1 ring-gray-200"
                  )}
                >
                  {pageNum}
                </button>
              ))}
            </div>

            <button
              onClick={() => setPage(Math.min(page + 1, totalPages))}
              disabled={page === totalPages || totalPages === 0}
              className={cx(
                "p-3 rounded-2xl ring-1 ring-gray-200 transition-colors",
                page === totalPages || totalPages === 0
                  ? "opacity-50 cursor-not-allowed bg-gray-50"
                  : "bg-white hover:bg-gray-50"
              )}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
